export interface NumberFormatOptions {
  /** Số chữ số thập phân tối đa cho phép. Mặc định: 2 */
  decimals?: number;
  /** Cho phép nhập số âm hay không. Mặc định: false */
  allowNegative?: boolean;
}

/**
 * Định dạng chuỗi số thô khi người dùng đang nhập liệu (live formatting):
 * - Dấu chấm (.) phân cách hàng nghìn: 1250000 -> "1.250.000"
 * - Dấu phẩy (,) hoặc dấu chấm vừa gõ (.) phân cách thập phân: 1250,5 -> "1.250,5"
 * - Giữ nguyên dấu phẩy cuối khi người dùng vừa gõ dấu phẩy (vd: "1.250,")
 * - Giữ dấu âm khi bắt đầu gõ "-"
 */
export function formatRawNumber(
  raw: string,
  options: NumberFormatOptions = {},
): string {
  if (!raw) return "";

  const { decimals = 2, allowNegative = false } = options;
  let trimmed = raw.trim();

  // Kiểm tra dấu âm
  const isNegative = allowNegative && trimmed.startsWith("-");

  // Xử lý riêng trường hợp chỉ mới gõ dấu âm "-"
  if (trimmed === "-") {
    return isNegative ? "-" : "";
  }

  // Nếu người dùng vừa gõ dấu chấm '.' ở cuối mà chưa có dấu phẩy: chuyển thành dấu phẩy
  if (decimals > 0 && !trimmed.includes(",") && trimmed.endsWith(".")) {
    trimmed = trimmed.slice(0, -1) + ",";
  }

  // Tách phần nguyên và phần thập phân (theo dấu phẩy ",")
  let intRaw = trimmed;
  let decRaw: string | null = null;

  if (trimmed.includes(",")) {
    const parts = trimmed.split(",");
    intRaw = parts[0] ?? "";
    if (decimals > 0) {
      decRaw = parts.slice(1).join("");
    }
  }

  // Làm sạch phần nguyên: chỉ giữ chữ số
  const intDigits = intRaw.replace(/\D/g, "");

  // Format phần nguyên với dấu chấm hàng nghìn
  let formattedInt = "";
  if (intDigits) {
    // Xóa số 0 thừa ở đầu nếu > 0 (ví dụ "0125" -> "125", nhưng giữ "0")
    const cleanLeadingZeros = intDigits.replace(/^0+(?=\d)/, "");
    formattedInt = cleanLeadingZeros.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  // Xử lý phần thập phân
  let formattedDec = "";
  if (decimals > 0 && decRaw !== null) {
    const decDigits = decRaw.replace(/\D/g, "").slice(0, decimals);
    formattedDec = `,${decDigits}`;
  }

  // Kết hợp kết quả
  if (!formattedInt && !formattedDec) {
    return isNegative ? "-" : "";
  }

  // Nếu phần nguyên rỗng nhưng có dấu phẩy (vd gõ ",5"), hiển thị "0,5"
  const finalInt = formattedInt || "0";
  const sign = isNegative ? "-" : "";

  return `${sign}${finalInt}${formattedDec}`;
}

/**
 * Chuyển chuỗi định dạng (ví dụ "1.250.000,5") thành giá trị số `number | null`.
 * Trong input chuẩn VN: dấu chấm '.' luôn là dấu phân cách hàng nghìn, dấu phẩy ',' là dấu thập phân.
 */
export function parseFormattedNumber(val: string | null | undefined): number | null {
  if (val === null || val === undefined || val === "") return null;
  const trimmed = String(val).trim();
  if (trimmed === "" || trimmed === "-") return null;

  const normalized = trimmed.replace(/\./g, "").replace(",", ".");
  const withoutTrailingDot = normalized.endsWith(".") ? normalized.slice(0, -1) : normalized;
  if (withoutTrailingDot === "" || withoutTrailingDot === "-") return null;

  const n = Number(withoutTrailingDot);
  return Number.isFinite(n) ? n : null;
}

/**
 * Định dạng một giá trị số `number | string` sẵn có thành chuỗi hiển thị theo chuẩn VN.
 */
export function formatNumberValue(
  val: number | string | null | undefined,
  options: NumberFormatOptions = {},
): string {
  if (val === null || val === undefined || val === "") return "";
  const num = typeof val === "number" ? val : parseFormattedNumber(String(val));
  if (num === null || Number.isNaN(num)) return "";

  const { decimals = 2 } = options;
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  const [intPart, decPart] = absNum.toString().split(".");
  const formattedInt = (intPart ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  if (decimals > 0 && decPart !== undefined && decPart.length > 0) {
    const slicedDec = decPart.slice(0, decimals);
    return `${isNegative ? "-" : ""}${formattedInt},${slicedDec}`;
  }

  return `${isNegative ? "-" : ""}${formattedInt}`;
}

/**
 * Tính toán vị trí con trỏ mới (Caret Position) sau khi định dạng lại chuỗi:
 * Dựa trên số lượng ký tự nội dung (chữ số, dấu phẩy, dấu âm) trước con trỏ cũ.
 */
export function calculateCaretPosition(
  rawBeforeCaret: string,
  newFormatted: string,
): number {
  // Đếm các ký tự có nghĩa (không phải dấu chấm phân cách hàng nghìn)
  const significantChars = rawBeforeCaret.replace(/\./g, "").length;

  let count = 0;
  for (let i = 0; i < newFormatted.length; i++) {
    if (newFormatted[i] !== ".") {
      count++;
    }
    if (count === significantChars) {
      return i + 1;
    }
  }

  return newFormatted.length;
}
